/**
 * @file buttonVariants.ts
 * @brief The `cva` recipe behind `<Button>`, on its own so it can be shared.
 *
 * @details
 * Split out of `Button.tsx` (02-08-2026). It lives here rather than beside the
 * component because a module that exports **both** a component and a value
 * loses React Fast Refresh — `@vitejs/plugin-react` can no longer hot-swap it
 * and full-reloads the page on every edit. `AlertDialog` styles its action /
 * cancel buttons from this recipe without rendering a `<Button>`, so it has to
 * be importable on its own.
 *
 * Prefer `<Button variant=… size=…>`; reach for the recipe only when you need
 * button styling on an element that is not a `<Button>` (a Radix action, a
 * `<Link>` that cannot take `asChild`).
 *
 * @dependencies
 * - class-variance-authority
 *
 * @references
 * - components/ui/Button.tsx
 * - components/ui/AlertDialog.tsx
 *
 * @author DES
 * @date 02-08-2026
 */

import { cva } from "class-variance-authority";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4",
        lg: "h-12 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);
